import { AccountType } from '../model/Enums.js';
import { convertInMatrix } from '../utils.js';
/**
 * A GroupsDataTableBuilder is used to setup and build two-dimensional arrays containing groups.
 *
 * @public
 */
export class GroupsDataTableBuilder {
    constructor(groups) {
        this.COMPARATOR = (g1, g2) => {
            let ret = this.getTypeIndex(this.getStringType(g1)) - this.getTypeIndex(this.getStringType(g2));
            if (ret === 0) {
                ret =
                    this.getHasChildrenIndex(g1.hasChildren()) -
                        this.getHasChildrenIndex(g2.hasChildren());
            }
            if (ret === 0) {
                ret = g1.getNormalizedName().localeCompare(g2.getNormalizedName());
            }
            return ret;
        };
        this.groups = groups;
        this.shouldAddProperties = false;
        this.shouldAddIds = false;
        this.shouldAddHiddenProperties = false;
        this.shouldShowTree = false;
        this.propertyKeys = [];
    }
    /**
     * Defines whether include custom group properties.
     *
     * @param include - Whether to include properties
     *
     * @returns This builder with respective include properties option, for chaining.
     */
    properties(include) {
        this.shouldAddProperties = include;
        return this;
    }
    /**
     * Defines whether include group ids.
     *
     * @param include - Whether to include ids
     *
     * @returns This builder with respective include ids option, for chaining.
     */
    ids(include) {
        this.shouldAddIds = include;
        return this;
    }
    /**
     * Defines whether to include hidden properties (keys ending with underscore "_").
     * Only relevant when {@link properties} is enabled.
     * Default is false — hidden properties are excluded.
     *
     * @param include - Whether to include hidden properties
     *
     * @returns This builder with respective option, for chaining.
     */
    hiddenProperties(include) {
        this.shouldAddHiddenProperties = include;
        return this;
    }
    /**
     * Defines whether to render groups as an indented tree instead of flat rows with a Parent column.
     * When enabled, child group names are indented by depth level and the Parent column is removed.
     * Default is false.
     *
     * @param enable - Whether to enable tree rendering
     *
     * @returns This builder with respective option, for chaining.
     */
    tree(enable) {
        this.shouldShowTree = enable;
        return this;
    }
    mapPropertyKeys() {
        this.propertyKeys = [];
        for (const group of this.groups) {
            for (const key of group.getPropertyKeys()) {
                if (!this.shouldAddHiddenProperties && key.endsWith('_')) {
                    continue;
                }
                if (this.propertyKeys.indexOf(key) <= -1) {
                    this.propertyKeys.push(key);
                }
            }
        }
        this.propertyKeys = this.propertyKeys.sort();
    }
    getStringType(group) {
        let groupType = group.getType() ? group.getType() + '' : undefined;
        if (!groupType) {
            groupType = group.isPermanent()
                ? AccountType.ASSET + '_' + AccountType.LIABILITY
                : AccountType.INCOMING + '_' + AccountType.OUTGOING;
        }
        return groupType;
    }
    getTypeIndex(type) {
        if (type === AccountType.ASSET + '_' + AccountType.LIABILITY) {
            return 0;
        }
        if (type === AccountType.ASSET) {
            return 1;
        }
        if (type === AccountType.LIABILITY) {
            return 2;
        }
        if (type === AccountType.INCOMING + '_' + AccountType.OUTGOING) {
            return 3;
        }
        if (type === AccountType.INCOMING) {
            return 4;
        }
        return 5;
    }
    getHasChildrenIndex(hasChildren) {
        return hasChildren ? 0 : 1;
    }
    /**
     * Builds a two-dimensional array containing all Groups.
     *
     * @returns A two-dimensional array containing all Groups
     */
    build() {
        let table = new Array();
        const groups = this.groups;
        groups.sort(this.COMPARATOR);
        let headers = [];
        if (this.shouldAddIds) {
            headers.push('Group Id');
        }
        headers.push('Name');
        headers.push('Type');
        if (!this.shouldShowTree) {
            headers.push('Parent');
        }
        if (this.shouldAddProperties) {
            this.mapPropertyKeys();
        }
        for (const group of groups) {
            if (group.isHidden() || group.getParent()) {
                continue;
            }
            table.push(this.buildGroupLine(group, 0));
            table = this.traverse(group, table, 1);
        }
        if (this.shouldAddProperties) {
            headers = headers.concat(this.propertyKeys);
        }
        table.unshift(headers);
        convertInMatrix(table);
        return table;
    }
    buildGroupLine(group, depth) {
        const line = [];
        if (this.shouldAddIds) {
            line.push(group.getId());
        }
        const name = this.shouldShowTree ? '  '.repeat(depth) + group.getName() : group.getName();
        line.push(name);
        line.push(this.getStringType(group));
        if (!this.shouldShowTree) {
            const parentName = group.getParent() ? group.getParent().getName() : '';
            line.push(parentName);
        }
        if (this.shouldAddProperties) {
            const properties = group.getProperties();
            for (const key of this.propertyKeys) {
                const propertyValue = properties[key];
                if (propertyValue) {
                    line.push(propertyValue);
                    continue;
                }
                line.push('');
            }
        }
        return line;
    }
    traverse(group, table, depth) {
        const children = group.getChildren();
        children.sort(this.COMPARATOR);
        for (const child of children) {
            table.push(this.buildGroupLine(child, depth));
            if (child.hasChildren()) {
                this.traverse(child, table, depth + 1);
            }
        }
        return table;
    }
}
//# sourceMappingURL=GroupsDataTableBuilder.js.map